import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import API from "../../services";
import { connect } from "react-redux";
import { RootState } from "../../models/RootState";
import { UserDetails } from "../../models/UserDetails";
import DownArrow from '../../assets/images/down-arrow.svg';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import "./DepositForm.scss";
import Dialog from "@mui/material/Dialog";
import CloseIcon from '@mui/icons-material/Close';
import { demoUser } from "../../utils/StringUtils";
import { useTranslation } from "../../hooks/useTranslation";
import { BRAND_NAME } from "../../constants/Branding";

interface PGItem {
  accountId: string;
  tgProviderId: string;
  category: string;
  pgName?: string;
  minDeposit: number;
  maxDeposit: number;
  minWithdrawal: number;
  maxWithdrawal: number;
}
interface DepositFormProps {
  activeTab: string;
  action: string;
  userDetails: UserDetails;
  withdrawable: any;
  pgList: PGItem[];
}

const WithdrawForm: React.FC<DepositFormProps> = (props) => {
  const { activeTab, userDetails, withdrawable, pgList } = props;
  const { t } = useTranslation();
  const isDemo = demoUser();
  const inputBoxClass =
    "px-4 py-3 mt-1.5 w-full text-sm font-medium text-white placeholder:text-text-color rounded-lg border border-solid bg-inputbox-color border-border-color focus:outline-none leading-[1.4rem]";

  // Helper function to check if user is created from back office
  // Format: {botId}-a... (e.g., 1844-anm777) vs regular: {botId}-{number}... (e.g., 1844-916281755762)
  const isBackOfficeUser = () => {
    const userKey = userDetails?.UserKey || sessionStorage.getItem("userKey");
    if (!userKey) return false;
    
    const parts = userKey.split("-");
    if (parts.length < 2) return false;
    
    // Check if the part after the dash starts with lowercase 'a'
    const afterDash = parts[1];
    return afterDash && /^a/.test(afterDash);
  };

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [accountType, setAccountType] = useState<string>("");
  const [accountHolderName, setAccountHolderName] = useState("");
  const [accountId, setAccountId] = useState("");
  const [bankName, setBankName] = useState("");
  const [ifscCode, setIfscCode] = useState("");
  const [upiId, setUpiId] = useState("");
  const [amount, setAmount] = useState<number>();
  const [activeCurType, setActiveCurType] = useState<string>('Fiat');
  const [selectedCurrency, setSelectedCurrency] = useState<any>();
  const [isOpen, setIsOpen] = useState(false);
  const [currList, setCurrList] = useState<any>([]);
  const [walletAddress, setWalletAddress] = useState<any>("");
  const [cryptoAmount, setCryptoAmount] = useState<number | undefined>(undefined);
  const [openForm, setOpenForm] = useState<any>(false);
  const [nickName, setNickName] = useState<any>("");
  const [bankAccountSelected, setBankAccountSelected] = useState<any>();
  const [accList, setAccList] = useState<any>([]);
  const [openAmForm, setOpenAmForm] = useState<any>(false);
  const [selectedPG, setSelectedPG] = useState<PGItem | null>(null);
  const [fiatPGList, setFiatPGList] = useState<PGItem[]>([]);
  const [cryptoPGList, setCryptoPGList] = useState<PGItem[]>([]);
  const isKing123Brand = BRAND_NAME?.toLowerCase() === 'king123';

  // Filter PG list based on active tab/categor

  // Set default selected PG when filtered list or tab changes
  // Filter PG list based on active tab/category and set default selected PG
  useEffect(() => {
    if (isDemo) return;
    const fiatList = pgList?.filter((pg: PGItem) => pg?.category?.toLowerCase() === 'fiat') ?? [];
    const cryptoList = pgList?.filter((pg: PGItem) => pg?.category?.toLowerCase() === 'crypto') ?? [];

    setFiatPGList(fiatList);
    setCryptoPGList(cryptoList);

    // Ensure active tab corresponds to an available list
    if (activeCurType === 'Fiat' && fiatList?.length === 0 && cryptoList?.length > 0) {
      setActiveCurType('Crypto');
      setSelectedPG(cryptoList[0] ?? null);
      return;
    }
    if (activeCurType === 'Crypto' && cryptoList?.length === 0 && fiatList?.length > 0) {
      setActiveCurType('Fiat');
      setSelectedPG(fiatList[0] ?? null);
      return;
    }

    // Set default selected PG based on current active tab
    if (activeCurType === 'Fiat') {
      setSelectedPG(fiatList?.length > 0 ? fiatList[0] : null);
    } else if (activeCurType === 'Crypto') {
      setSelectedPG(cryptoList.length > 0 ? cryptoList[0] : null);
    }
  }, [pgList, activeCurType]);

  // Call getCurrList when selectedPG changes and we're in Crypto tab
  useEffect(() => {
    if (isDemo) return;
    if (activeCurType === "Crypto" && selectedPG && cryptoPGList?.length > 0) {
      // Only call getCurrList if selectedPG is part of cryptoPGList
      const isCryptoPG = cryptoPGList.some(pg => pg.tgProviderId === selectedPG.tgProviderId);
      if (isCryptoPG) {
        getCurrList();
      }
    }
  }, [selectedPG, activeCurType]);

  // Helper function to reset all account form fields
  const resetAccountFormFields = () => {
    setAccountType("");
    setAccountHolderName("");
    setAccountId("");
    setBankName("");
    setIfscCode("");
    setUpiId("");
    setNickName("");
  };

  const allFieldsFilled = () => {
    if (!accountType) return false;
    
    if (accountType === "bank account") {
      return (
        accountHolderName &&
        accountId &&
        bankName &&
        ifscCode &&
        nickName &&
        nickName?.length > 2 &&
        (isBackOfficeUser() || userDetails?.MobileNumber || userDetails?.IsContactOptional)
      );
    } else if (accountType === "upi") {
      return (
        upiId &&
        nickName &&
        nickName?.length > 2 &&
        (isBackOfficeUser() || userDetails?.MobileNumber || userDetails?.IsContactOptional)
      );
    }
    return false;
  };

  const allCryptoFieldsFilled = () => {
    return (
      selectedCurrency &&
      walletAddress &&
      cryptoAmount &&
      (
        userDetails?.IsPremium ||
        (
          cryptoAmount >= (selectedPG?.minWithdrawal ?? 0) / selectedCurrency?.conversions?.withdrawal_exchange_rate &&
          cryptoAmount <= Math.min(
            selectedPG?.maxWithdrawal ?? 0,
            withdrawable ?? 0
          ) / selectedCurrency?.conversions?.withdrawal_exchange_rate
        )
      ) &&
      (isBackOfficeUser() || userDetails?.MobileNumber || userDetails?.IsContactOptional)
    );
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isDemo) return;

    setLoading(true);
    setOpenAmForm(false)
    try {
      const response = await API.post(`/account/processWithdrawal`, {
        aoountName: bankAccountSelected?.name,
        amount: amount,
        uKey: sessionStorage.getItem("userKey"),
        tgProviderId: selectedPG?.tgProviderId,
        accountId: selectedPG?.accountId,
        accountType: bankAccountSelected?.type,
      });

      const data = await response.data;
      if (data.status !== "RS_OK") {
        toast.error(`${data.errorDescription}`);
      } else {
        toast.success(t("wallet.messages.transactionSavedSuccessfully"));
      }

      setAmount(0);
    } catch (error) {
      console.error("There was a problem with the fetch operation:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAccountSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isDemo) return;

    setLoading(true);
    setOpenForm(false)
    try {
      const requestBody: any = {
        name: nickName,
        type: accountType,
        actHolderName: accountType === "bank account" ? accountHolderName : "",
        actNum: accountType === "bank account" ? accountId : "",
        bName: accountType === "bank account" ? bankName : "",
        ifsc: accountType === "bank account" ? ifscCode : "",
        upiId: accountType === "upi" ? upiId : "",
      };

      const response = await API.post(`/users/add-bank-account`, requestBody);

      const data = await response.data;
      if (data.status !== "RS_OK") {
        toast.error(`${data.errorDescription}`);
      } else {
        getAccList();
        toast.success(t("wallet.messages.accountAddedSuccessfully"));
      }
      // Reset all fields
      resetAccountFormFields();
    } catch (error) {
      console.error("There was a problem with the fetch operation:", error);
    } finally {
      setLoading(false);
    }
  };

  const cryptoWithdrawal = async () => {
    if (isDemo) return;
    setLoading(true);
    try {
      const response = await API.post(`/account/crypto-Withdrawal`, {
        amount: cryptoAmount,
        cryptoCurrency: selectedCurrency?.cryptoCurrency,
        network_id: selectedCurrency?.networkId,
        walletAddress: walletAddress,
        tgProviderId: selectedPG?.tgProviderId,
        accountId: selectedPG?.accountId,
        rate: selectedCurrency?.conversions?.withdrawal_exchange_rate,
      });

      const data = await response.data;
      if (data.status !== "RS_OK") {
        toast.error(`${data.errorDescription}`);
      } else {
        toast.success(t("wallet.messages.transactionSavedSuccessfully"));
      }
    } catch (error) {
      console.error("There was a problem with the fetch operation:", error);
    } finally {
      setLoading(false);
    }
  }



  const getCurrList = async () => {
    if (isDemo) return;
    let response;
    try {
      response = await API.post(`account/get-crypto-currencies`, {
        tgProviderId: selectedPG?.tgProviderId,
        accountId: selectedPG?.accountId,
      });
    } catch (e) {
      return;
    }
    if (response?.data?.status !== "RS_OK") {
      toast?.error(response?.data?.errorDescription)
    }
    if (response && response.data?.currenciesList) {
      setCurrList(response?.data?.currenciesList)
    }
  }

  const getAccList = async () => {
    if (isDemo) return;
    let response;
    try {
      response = await API.post(`/users/get-bank-accounts`, {
      });
    } catch (e) {
      return;
    }
    if (response?.data?.status !== "RS_OK") {
      toast?.error(response?.data?.errorDescription)
    }
    if (response && response.data?.bankAccounts) {
      const filteredAccounts = response.data.bankAccounts.filter((account: any) => account.createdBy === "USER");
      setAccList(filteredAccounts)
    }
  }

  const handleCurrencySelect = (curr: any) => {
    if (isDemo) return;
    setSelectedCurrency(curr);
    setIsOpen(false);
  };

  const toggleDropdown = () => setIsOpen(!isOpen);

  const handleTabClick = (tab: any) => {
    if (isDemo) return;
    // Prevent switching to a tab with no gateways
    if (tab === 'Fiat' && fiatPGList.length === 0) return;
    if (tab === 'Crypto' && cryptoPGList.length === 0) return;

    setActiveCurType(tab);
    if (tab === 'Fiat') {
      setSelectedPG(fiatPGList[0] ?? null);
    } else if (tab === 'Crypto') {
      setSelectedPG(cryptoPGList[0] ?? null);
    }
  };

  useEffect(() => {
    if (isDemo) return;
    if (activeCurType === "Fiat") {
      getAccList();
    }
  }, [activeCurType])


  const tabs = [
    {
      key: "Fiat",
      label: t("wallet.fiat"),
      content: (
        <>
          {!isBackOfficeUser() && (!userDetails?.MobileNumber && !userDetails?.IsContactOptional) && (
            <div className="text-lg mb-1 mb-4">
              {t("wallet.messages.pleaseShareContactBeforeWithdraw")}
            </div>
          )}

          {
            (isBackOfficeUser() || userDetails?.MobileNumber || userDetails?.IsContactOptional) && (
              <>
                <header className="flex gap-3 items-center mt-1 self-start text-base font-medium text-center text-white mb-[0.5rem] w-full justify-end">
                  <button
                    onClick={() => setOpenForm(true)}
                    className="flex items-center justify-between px-3 py-2 text-xs font-medium text-white whitespace-nowrap rounded-lg border border-solid bg-inputbox-color border-slate-800"
                  >
                    <span>{t("wallet.addBankDetails")}</span>
                  </button>
                </header>

                {
                  accList && (
                    accList?.map((row: any) => (
                      <>
                        <div className="flex gap-5 justify-between p-4 mb-2 w-full rounded-lg bg-meitms-color"
                          onClick={() => { setBankAccountSelected(row); setOpenAmForm(true) }}
                        >
                          <div className="flex gap-3 w-full justify-between items-center font-medium h-[41px]">
                            <div className="text-sm text-white">{row?.name}</div>
                            <div className="flex flex-col items-end gap-1">
                              <div className={`text-sm text-white`}>{row?.type === "bank account" ? row?.accountNumber : row?.upiId ? row?.upiId : ""}</div>
                              <div className={`text-xs text-white`}>{row?.type || "bank account"}</div>
                            </div>
                          </div>
                        </div>
                      </>
                    ))
                  )
                }
              </>
            )
          }

          {
            openForm && (
              <Dialog
                fullScreen={false}
                open={openForm}
                onClose={() => {
                  setOpenForm(false);
                  resetAccountFormFields();
                }}
                PaperProps={{
                  style: {
                    position: 'absolute',
                    top: '2rem'
                  }
                }}
              >
                <div className="withdrawdialog-bg bg-inputbox-color relative p-4">
                  <CloseIcon
                    className={`absolute top-3 right-3 text-2xl font-semibold text-white`}
                    onClick={() => {
                      setOpenForm(false);
                      resetAccountFormFields();
                    }}
                  />
                  <div>
                    <div className={`text-white`}>
                      {t("wallet.addBankDetails")}
                    </div>
                    <div>
                      <>
                        <form className="flex flex-col w-full" onSubmit={handleAccountSubmit}>
                          <label
                            htmlFor="accountType"
                            className="block mt-3 text-sm text-zinc-400"
                          >
                            {t("wallet.accountType")}
                          </label>
                          <select
                            id="accountType"
                            className={`${inputBoxClass} ${(!isBackOfficeUser() && !userDetails?.MobileNumber && !userDetails?.IsContactOptional) ? "bg-gray-100 cursor-not-allowed" : ""
                              }`}
                            value={accountType}
                            onChange={(e) => {
                              const newAccountType = e.target.value;
                              // Reset all fields when type changes (including accountType)
                              resetAccountFormFields();
                              // Set the new account type after resetting
                              setAccountType(newAccountType);
                            }}
                            disabled={!isBackOfficeUser() && !userDetails?.MobileNumber && !userDetails?.IsContactOptional}
                          >
                            <option value="">{t("wallet.selectAccountType") || "Select Account Type"}</option>
                            <option value="bank account">{t("wallet.bankAccount") || "Bank Account"}</option>
                            {!isKing123Brand && (
                              <option value="upi">{t("wallet.upi") || "UPI"}</option>
                            )}
                          </select>

                          {accountType === "bank account" && (
                            <>
                              <label
                                htmlFor="accountHolderName"
                                className="block mt-3 text-sm text-zinc-400"
                              >
                                {t("wallet.accountHolderName")}
                              </label>
                              <input
                                type="text"
                                id="accountHolderName"
                                className={`${inputBoxClass} ${(!isBackOfficeUser() && !userDetails?.MobileNumber && !userDetails?.IsContactOptional) ? "bg-gray-100 cursor-not-allowed" : ""
                                  }`}
                                placeholder={t("wallet.accountHolderName")}
                                value={accountHolderName}
                                onChange={(e) => setAccountHolderName(e.target.value)}
                                disabled={!isBackOfficeUser() && !userDetails?.MobileNumber && !userDetails?.IsContactOptional}
                              />

                              <label htmlFor="accountId" className="block mt-3 text-sm text-zinc-400">
                                {t("wallet.accountNumber")}
                              </label>
                              <input
                                type="number"
                                id="accountId"
                                className={`${inputBoxClass} ${(!isBackOfficeUser() && !userDetails?.MobileNumber && !userDetails?.IsContactOptional) ? "bg-gray-100 cursor-not-allowed" : ""
                                  }`}
                                placeholder={t("wallet.accountNumber")}
                                value={accountId}
                                onChange={(e) => setAccountId(e.target.value)}
                                disabled={!isBackOfficeUser() && !userDetails?.MobileNumber && !userDetails?.IsContactOptional}
                              />

                              <label htmlFor="bankName" className="block mt-3 text-sm text-zinc-400">
                                {t("wallet.bankName")}
                              </label>
                              <input
                                type="text"
                                id="bankName"
                                className={`${inputBoxClass} ${(!isBackOfficeUser() && !userDetails?.MobileNumber && !userDetails?.IsContactOptional) ? "bg-gray-100 cursor-not-allowed" : ""
                                  }`}
                                placeholder={t("wallet.bankName")}
                                value={bankName}
                                onChange={(e) => setBankName(e.target.value)}
                                disabled={!isBackOfficeUser() && !userDetails?.MobileNumber && !userDetails?.IsContactOptional}
                              />

                              <label htmlFor="ifscCode" className="block mt-3 text-sm text-zinc-400">
                                {t("wallet.ifscCode")}
                              </label>
                              <input
                                type="text"
                                id="ifscCode"
                                className={`${inputBoxClass} ${(!isBackOfficeUser() && !userDetails?.MobileNumber && !userDetails?.IsContactOptional) ? "bg-gray-100 cursor-not-allowed" : ""
                                  }`}
                                placeholder={t("wallet.ifscCode")}
                                value={ifscCode}
                                onChange={(e) => setIfscCode(e.target.value)}
                                disabled={!isBackOfficeUser() && !userDetails?.MobileNumber && !userDetails?.IsContactOptional}
                              />
                            </>
                          )}

                          {accountType === "upi" && (
                            <>
                              <label htmlFor="upiId" className="block mt-3 text-sm text-zinc-400">
                                {t("wallet.upiId") || "UPI ID"}
                              </label>
                              <input
                                type="text"
                                id="upiId"
                                className={`${inputBoxClass} ${(!isBackOfficeUser() && !userDetails?.MobileNumber && !userDetails?.IsContactOptional) ? "bg-gray-100 cursor-not-allowed" : ""
                                  }`}
                                placeholder={t("wallet.upiId") || "Enter UPI ID"}
                                value={upiId}
                                onChange={(e) => setUpiId(e.target.value)}
                                disabled={!isBackOfficeUser() && !userDetails?.MobileNumber && !userDetails?.IsContactOptional}
                              />
                            </>
                          )}

                          {accountType && (
                            <>
                              <label htmlFor="nickName" className="block mt-3 text-sm text-zinc-400">
                                {t("wallet.nickName")}
                              </label>
                              <input
                                type="text"
                                id="nickName"
                                className={`${inputBoxClass} ${(!isBackOfficeUser() && !userDetails?.MobileNumber && !userDetails?.IsContactOptional) ? "bg-gray-100 cursor-not-allowed" : ""}`}
                                placeholder={t("wallet.enterYourName")}
                                value={nickName}
                                onChange={(e) => {
                                  const alphanumericValue = e.target.value.replace(/[^a-zA-Z0-9]/g, "");
                                  setNickName(alphanumericValue);
                                }}
                                disabled={!isBackOfficeUser() && !userDetails?.MobileNumber && !userDetails?.IsContactOptional}
                              />
                              {
                                nickName != null && nickName?.length < 3 && (
                                  <span className="mt-1 text-sm text-white-500">
                                    {t("wallet.messages.nicknameValidation")}
                                  </span>
                                )
                              }
                            </>
                          )}

                          <button
                            type="submit"
                            className={`p-2.5 mt-4 w-full text-sm text-white whitespace-nowrap bg-border-color rounded-lg border border-border-color border-solid leading-[1.4rem] ${loading || !allFieldsFilled() ? "opacity-50" : ""
                              }`}
                            disabled={loading || !allFieldsFilled()}
                          >
                            {(isBackOfficeUser() || userDetails?.MobileNumber || userDetails?.IsContactOptional)
                              ? loading
                                ? t("wallet.messages.submitting")
                                : t("common.submit")
                              : t("wallet.addMobileNumber")}
                          </button>
                        </form>
                      </>
                    </div>
                  </div>
                </div>
              </Dialog>
            )
          }

          {
            openAmForm && (
              <Dialog
                fullScreen={false}
                open={openAmForm}
                onClose={() => { setOpenAmForm(false); setAmount(0) }}
                PaperProps={{
                  style: {
                    position: 'absolute',
                    top: '2rem'
                  }
                }}
              >
                <div className="withdrawdialog-bg bg-inputbox-color relative p-4">
                  <CloseIcon
                    className="absolute top-3 right-3 text-white text-2xl font-semibold"
                    onClick={() => { setOpenAmForm(false); setAmount(0) }}
                  />
                  <div>
                    <div>
                      {t("wallet.enterWithdrawalAmount")}
                    </div>
                    <div>
                      <>
                        <form className="flex flex-col w-full" onSubmit={handleSubmit}>
                          <label
                            htmlFor="accountHolderName"
                            className="block mt-3 text-sm text-zinc-400"
                          >
                            {t("wallet.name")}
                          </label>
                          <input
                            type="text"
                            id="accountHolderName"
                            className={`${inputBoxClass} ${(!isBackOfficeUser() && !userDetails?.MobileNumber && !userDetails?.IsContactOptional) ? "bg-gray-100 cursor-not-allowed" : ""
                              }`}
                            placeholder={t("wallet.accountHolderName")}
                            value={bankAccountSelected?.name}
                            onChange={(e) => setAccountHolderName(e.target.value)}
                            disabled
                          />

                          <label htmlFor="amount" className="block mt-3 text-sm text-zinc-400">
                            {t("common.amount")}
                          </label>
                          <input
                            type="number"
                            id="amount"
                            className={`${inputBoxClass} ${(!isBackOfficeUser() && !userDetails?.MobileNumber && !userDetails?.IsContactOptional) ? "bg-gray-100 cursor-not-allowed" : ""
                              }`}
                            placeholder={t("wallet.enterYourAmount")}
                            value={amount}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value);
                              setAmount(value);
                            }}
                            disabled={!isBackOfficeUser() && !userDetails?.MobileNumber && !userDetails?.IsContactOptional}
                          />
                          {!userDetails?.IsPremium && (withdrawable ?? 0) === 0 && (
                            <span className="mt-1 text-sm text-orange-500">
                              {t("wallet.messages.noWithdrawableAmountAvailable")}
                            </span>
                          )}
                          {!userDetails?.IsPremium && (withdrawable ?? 0) > 0 && (withdrawable ?? 0) < (selectedPG?.minWithdrawal ?? 0) && (
                            <span className="mt-1 text-sm text-orange-500">
                              {t("wallet.messages.increaseWithdrawableAmount").replace("{amount}", String(selectedPG?.minWithdrawal || 0))}
                            </span>
                          )}
                          {amount != null && !userDetails?.IsPremium && (withdrawable ?? 0) > 0 && (withdrawable ?? 0) >= (selectedPG?.minWithdrawal ?? 0) && (amount < (selectedPG?.minWithdrawal ?? 0) || amount > Math.min(selectedPG?.maxWithdrawal ?? 0, withdrawable ?? 0)) && (
                            <span className="mt-1 text-sm text-orange-500">
                              {t("wallet.messages.amountMustNotExceed").replace("{amount}", String(Math.min(selectedPG?.maxWithdrawal ?? 0, withdrawable ?? 0)))}
                              <br />

                              {t("wallet.messages.minBalanceToWithdraw").replace("{amount}", String(selectedPG?.minWithdrawal || 0))}
                            </span>
                          )}

                          {
                            userDetails?.IsPremium && amount != null && amount > (withdrawable ?? 0) && (
                              <span className="mt-1 text-sm text-orange-500">
                                {t("wallet.messages.amountMustNotExceed").replace("{amount}", String(withdrawable))}
                                <br />
                              </span>
                            )
                          }

                          <button
                            type="submit"
                            className={`p-2.5 mt-4 w-full text-sm text-white whitespace-nowrap bg-border-color rounded-lg border border-border-color border-solid leading-[1.4rem] ${(loading || amount == null || isNaN(amount) || (!userDetails?.IsPremium && (amount < (selectedPG?.minWithdrawal ?? 0) || amount > Math.min(selectedPG?.maxWithdrawal ?? 0, withdrawable ?? 0))) || (userDetails?.IsPremium && amount > (withdrawable ?? 0))) ? "opacity-50" : ""
                              }`}
                            disabled={loading || amount == null || isNaN(amount) || (!userDetails?.IsPremium && (amount < (selectedPG?.minWithdrawal ?? 0) || amount > Math.min(selectedPG?.maxWithdrawal ?? 0, withdrawable ?? 0))) || (userDetails?.IsPremium && amount > (withdrawable ?? 0))}
                          >
                            {(isBackOfficeUser() || userDetails?.MobileNumber || userDetails?.IsContactOptional)
                              ? loading
                                ? t("wallet.messages.submitting")
                                : t("common.submit")
                              : t("wallet.addMobileNumber")}
                          </button>
                        </form>
                      </>
                    </div>
                  </div>
                </div>
              </Dialog>
            )
          }

          <div className="h-[180px] p-4">
          </div>
        </>
      )
    },
    {
      key: "Crypto",
      label: t("wallet.crypto"),
      content: (
        <>
          {
            cryptoPGList.length === 0 && activeCurType === "Crypto" ? (
              <div className="text-lg mb-1 mb-4">
                {t("wallet.messages.cryptoGatewayNotAvailable")}
              </div>
            ) : (
              <>
                {
                  currList.length === 0 ? (
                    <div className="text-lg m-2 text-center">
                      {t("wallet.messages.noCryptoCurrencyFound")}
                    </div>
                  ) : (
                    <>
                      <label
                        htmlFor="accountHolderName"
                        className="block mt-3 text-sm text-zinc-400"
                      >
                        {t("wallet.selectCurrencyLabel")}
                      </label>
                      <div className="relative w-full h-[30px]">
                        <button
                          onClick={toggleDropdown}
                          className="flex items-center mt-2 h-full justify-between px-3 py-2 w-full text-xs font-medium text-white whitespace-nowrap rounded-lg border border-solid bg-inputbox-color border-border-color"
                        >
                          <span className="truncate">
                            {selectedCurrency?.cryptoCurrency
                              ? `${selectedCurrency?.cryptoCurrency} (${selectedCurrency?.blockChain})`
                              : ""}
                          </span>
                          <img
                            loading="lazy"
                            src={DownArrow}
                            alt=""
                            className="shrink-0 ml-2 w-2.5 aspect-[1.67]"
                          />
                        </button>

                        {isOpen && (
                          <ul className="absolute z-10 w-full left-0 top-full mt-1 bg-inputbox-color border border-slate-800 rounded-lg shadow-lg">
                            {currList?.map((curr: any) => (
                              <li
                                key={curr}
                                onClick={() => handleCurrencySelect(curr)}
                                className="px-3 py-2 text-xs text-white hover:bg-zinc-700 cursor-pointer"
                              >
                                {`${curr?.cryptoCurrency} (${curr?.blockChain})`}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>

                      <label htmlFor="amount" className="block mt-3 text-sm text-zinc-400">
                        {t("common.amount")}
                      </label>
                      <input
                        type="number"
                        id="amount"
                        className={inputBoxClass}
                        placeholder={t("wallet.enterYourAmount")}
                        value={cryptoAmount}
                        onChange={(e) => {
                          const value =
                            e.target.value === "" ? 0 : parseFloat(e.target.value);
                          setCryptoAmount(value);
                        }}
                        disabled={!selectedCurrency}
                      />

                      {!selectedCurrency && (
                        <p className="text-sm text-orange-500 mt-1">{t("wallet.messages.selectACurrency")}</p>
                      )}

                      {!userDetails?.IsPremium && (withdrawable ?? 0) === 0 && (
                        <span className="mt-1 text-sm text-orange-500">
                          {t("wallet.messages.noWithdrawableAmountAvailable")}
                        </span>
                      )}
                      {!userDetails?.IsPremium && (withdrawable ?? 0) > 0 && (withdrawable ?? 0) < (selectedPG?.minWithdrawal ?? 0) && (
                        <span className="mt-1 text-sm text-orange-500">
                          {t("wallet.messages.increaseWithdrawableAmount").replace("{amount}", String(selectedPG?.minWithdrawal || 0))}
                        </span>
                      )}
                      {cryptoAmount != null && !userDetails?.IsPremium && (withdrawable ?? 0) > 0 && (withdrawable ?? 0) >= (selectedPG?.minWithdrawal ?? 0) && (cryptoAmount < (selectedPG?.minWithdrawal ?? 0) / selectedCurrency?.conversions?.withdrawal_exchange_rate || cryptoAmount > Math.min(selectedPG?.maxWithdrawal ?? 0, withdrawable ?? 0) / selectedCurrency?.conversions?.withdrawal_exchange_rate) && (
                        <span className="mt-1 text-sm text-orange-500">
                          {t("wallet.messages.amountMustBeBetweenCrypto").replace("{min}", String((selectedPG?.minWithdrawal ?? 0) / selectedCurrency?.conversions?.withdrawal_exchange_rate)).replace("{max}", String(Math.min(selectedPG?.maxWithdrawal ?? 0, withdrawable ?? 0) / selectedCurrency?.conversions?.withdrawal_exchange_rate))}
                        </span>
                      )}

                      {userDetails?.IsPremium && cryptoAmount != null && cryptoAmount > (withdrawable ?? 0) / selectedCurrency?.conversions?.withdrawal_exchange_rate && (
                        <span className="mt-1 text-sm text-orange-500">
                          {t("wallet.messages.amountMustNotExceed").replace("{amount}", String((withdrawable ?? 0) / selectedCurrency?.conversions?.withdrawal_exchange_rate))}.
                        </span>
                      )}

                      <label htmlFor="walletAddress" className="block mt-3 text-sm text-zinc-400">
                        {t("wallet.enterYourWalletAddress")}
                      </label>
                      <input
                        type="text"
                        id="walletAddress"
                        className={inputBoxClass}
                        placeholder={t("wallet.enterYourWalletAddress")}
                        value={walletAddress}
                        onChange={(e) => setWalletAddress(e.target.value)}
                      />

                      <button
                        type="submit"
                        className={`p-2.5 mt-4 w-full text-sm text-white whitespace-nowrap bg-border-color rounded-lg border border-border-color border-solid leading-[1.4rem] ${loading || !allCryptoFieldsFilled() ? "opacity-50" : ""
                          }`}
                        disabled={loading || !allCryptoFieldsFilled()}
                        onClick={() => cryptoWithdrawal()}
                      >
                        {(isBackOfficeUser() || userDetails?.MobileNumber || userDetails?.IsContactOptional) ? (loading ? t("wallet.messages.submitting") : t("common.submit")) : t("wallet.addMobileNumber")}
                      </button>
                      <div className="h-[200px] p-4">
                      </div>
                    </>
                  )
                }
              </>
            )
          }
        </>
      )
    },

  ]
  // Only show tabs that have corresponding list items
  const availableTabs = tabs.filter((tab) =>
    (tab.key === 'Fiat' && fiatPGList.length > 0) ||
    (tab.key === 'Crypto' && cryptoPGList.length > 0)
  );
  return (
    <>
      {isDemo ? (
        <div className="p-4 rounded-lg text-center">
          <p className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-400 font-medium">
            {t("wallet.messages.demoAccountWithdrawalMessage")}
          </p>
        </div>
      ) : (
        <div className="deposit-tabs">
          <div className="wallet-nav">
          <div className="nav nav-pills" role="tablist">
            {availableTabs.map((tab) => (
              <div className="nav-item" key={tab.key}>
                <a
                  role="tab"
                  className={`nav-link ${activeCurType === tab.key ? "active" : ""}`}
                  onClick={() => handleTabClick(tab.key)}
                >
                  {tab.label}
                </a>
              </div>
            ))}
          </div>
          {/* PG List display below tab label */}
          {activeCurType === "Fiat" && fiatPGList.length > 0 && (
            <div className="nav nav-pills pt-2" role="tablist">
              {fiatPGList?.map((pg: any) => (
                <div className="" key={pg.accountId}>
                  <a
                    role="tab"
                    className={`nav-link pg-tab ${selectedPG?.tgProviderId === pg?.tgProviderId ? "active" : ""}`}
                    onClick={() => setSelectedPG(pg)}
                  >
                    {pg?.applicationName}
                  </a>
                </div>
              ))}
            </div>
          )}

          {activeCurType === "Crypto" && cryptoPGList.length > 0 && (
            <div className="nav nav-pills pt-2" role="tablist">
              {cryptoPGList?.map((pg: any) => (
                <div className="" key={pg.accountId}>
                  <a
                    role="tab"
                    className={`nav-link pg-tab ${selectedPG?.tgProviderId === pg?.tgProviderId ? "active" : ""}`}
                    onClick={() => setSelectedPG(pg)}
                  >
                    {pg?.applicationName}
                  </a>
                </div>
              ))}
            </div>
          )}
          <div className="tab-content">
            {availableTabs.map((tab) => (
              <div
                key={tab.key}
                className={`tab-pane fade ${activeCurType === tab.key ? "show active" : ""
                  }`}
                id={`tabs-tabpane-${tab.key}`}
                role="tabpanel"
              >
                {activeCurType === tab.key && tab.content}
              </div>
            ))}
          </div>
          </div>
        </div>
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    userDetails: state.userDetails?.userDetails,
  };
};


export default connect(mapStateToProps)(WithdrawForm);
